import { useState, useMemo } from 'react';
import { getDefaultModelConfig, simulateScenarios, ModelConfig } from './utils/financialEngine';
import { downloadJson, downloadCsv } from './utils/exportHelpers';
import { Navbar } from './components/Navbar';
import { KPICards } from './components/KPICards';
import { TrajectoryChart } from './components/TrajectoryChart';
import { InputPanel } from './components/InputPanel';
import { ComparisonTable } from './components/ComparisonTable';
import { Info, Sparkles } from 'lucide-react';

export function App() {
  const [config, setConfig] = useState<ModelConfig>(getDefaultModelConfig());

  const simulation = useMemo(() => {
    return simulateScenarios(config);
  }, [config]);

  const handleReset = () => {
    setConfig(getDefaultModelConfig());
  };

  const handleExportJson = () => {
    downloadJson(config, simulation);
  };

  const handleExportCsv = () => {
    downloadCsv(simulation);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      <Navbar
        onReset={handleReset}
        onExportJson={handleExportJson}
        onExportCsv={handleExportCsv}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Top Hero Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-cyan-950/40 to-slate-900 border border-slate-800 p-6 shadow-2xl">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1 max-w-2xl">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs font-medium mb-1">
                <Sparkles className="w-3.5 h-3.5" />
                Real Estate Decision Engine
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold font-heading text-white tracking-tight">
                Keep & Rent vs. Sell & Buy Simulator
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Analyze 3, 5, and 10-year net worth trajectories between keeping Home #1 as a rental property versus selling it to fund Home #2 down payment.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-mono text-slate-400 bg-slate-950/80 p-3 rounded-xl border border-slate-800/80">
              <div className="flex items-center gap-1">
                <Info className="w-3.5 h-3.5 text-cyan-400" />
                <span>Sec 121 Tax Exclusion Active</span>
              </div>
            </div>
          </div>
        </div>

        {/* Real-time Executive KPI Cards */}
        <KPICards simulation={simulation} />

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Interactive Input Controls */}
          <div className="lg:col-span-5 space-y-6">
            <InputPanel config={config} onChange={setConfig} />
          </div>

          {/* Right Column: Dynamic Visualizations & Annual Schedule */}
          <div className="lg:col-span-7 space-y-6">
            <TrajectoryChart trajectory={simulation.trajectory} />
            <ComparisonTable trajectory={simulation.trajectory} />
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-800/60 py-6 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p>© 2026 EquityHorizon Real Estate Financial Modeler. Powered by Cloudflare Pages.</p>
          <p className="font-mono text-[11px] text-slate-600">Built with React 19, Vite, Tailwind CSS & Recharts</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
